import React from 'react';
import { Link } from 'react-router-dom';
import { INSTAGRAM_URL, SUPPORT_EMAIL } from '../index';

/**
 * Footer — the colophon of the case file. Dense mono, quiet.
 *
 * No logo lockup, no social-icon row, no newsletter form, no "made with"
 * line. The wordmark is lowercase mono; the icon lives in the nav only. The
 * handle renders as mono text rather than a lucide glyph — brand-correct and
 * it saves an icon import from the landing bundle.
 *
 * Hover reveals an underline as well as brightening the colour: a state
 * carried by colour alone fails the colourblind law.
 *
 * Every route below is verified to resolve in App.js: /privacy, /terms,
 * /cookies, /faq, /blog. A 404 in the footer of a privacy-forward site is a
 * credibility failure.
 */

const linkClass =
  'cp-mono text-[length:var(--fs-mono)] no-underline transition-colors duration-150 ' +
  'hover:text-paper hover:underline underline-offset-4 ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-gold-400';

const LINKS = [
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Cookies', to: '/cookies' },
  { label: 'Support', href: `mailto:${SUPPORT_EMAIL}` },
  { label: 'FAQ', to: '/faq' },
  { label: 'Blog', to: '/blog' },
];

function Sep() {
  return (
    <span aria-hidden="true" className="cp-mono text-[length:var(--fs-mono)] text-ink-3">
      ·
    </span>
  );
}

export function Footer() {
  return (
    <footer className="relative isolate bg-void">
      <div className="mx-auto w-full max-w-[1200px] px-5 md:px-10 xl:px-16">
        <span aria-hidden="true" className="block h-px w-full bg-[color:var(--cp-hairline)]" />

        <div className="flex flex-col gap-8 py-12 lg:flex-row lg:items-start lg:justify-between">
          <p className="font-mono text-[14px] lowercase tracking-[0.06em] text-paper">
            crossedpaths.ai
          </p>

          <div className="flex flex-col gap-4 lg:items-end">
            <nav aria-label="Footer" className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {LINKS.map((link, i) => (
                <React.Fragment key={link.label}>
                  {i > 0 && <Sep />}
                  {link.to ? (
                    <Link to={link.to} className={linkClass}>
                      {link.label}
                    </Link>
                  ) : (
                    <a href={link.href} className={linkClass}>
                      {link.label}
                    </a>
                  )}
                </React.Fragment>
              ))}
            </nav>

            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              @crossedpathsai
            </a>
          </div>
        </div>

        {/* text-2, not text-3. This is real readable content, not decoration,
            and text-3 measures 4.05:1 here — under the AA bar. A1 permits
            text-3 only on aria-hidden decoration; the · separators below
            qualify, this line does not. */}
        <p className="cp-mono pb-12 text-[length:var(--fs-mono)] text-ink-2">
          © {new Date().getFullYear()} Crossed Paths AI LLC · All rights reserved
        </p>
      </div>
    </footer>
  );
}

export default Footer;
